import { useEffect, useState } from 'react';
import cx from 'classnames';

// Types
import { PaymentDeductionsItemProps } from './PaymentDeductionsItem.types';

// Config
import paymentTypeConstants from '@constants/paymentTypeConstants';
import last3Digits from '@utils/string/lastDigitsHelper';

// Components
import PaymentDeductionsItemActionButton from './PaymentDeductionsItemActionButton';
import HiddenAlert from '@widgets/accessibility/hiddenalert';

// Styles
import styles from './payment-deductions-item.scss';

const PaymentDeductionsItem = ({
  alert = false,
  amount = '',
  applyGiftPaymentFailed = false,
  balance = '',
  cardNumber = '',
  id = '',
  isLoading: propsIsLoading = false,
  isRemovable = false,
  isRemoved = false,
  onRemove = () => {},
  onUndo = () => {},
  orderTotalCovered = false,
  promoCodeLoadingCancelled = false,
  serialNumber = '',
  showUndo = false,
  type,
}: PaymentDeductionsItemProps) => {
  const [giftOptionRemoved, setGiftOptionRemoved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oldPropsIsRemoved, setOldPropsIsRemoved] = useState(false);
  const isGiftCard = type === paymentTypeConstants.GIFT_CARD;
  const giftOptionNumber = isGiftCard ? cardNumber : serialNumber;
  const endingIn = last3Digits(giftOptionNumber);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (promoCodeLoadingCancelled) {
      setIsLoading(false);
    } else if (isRemoved !== oldPropsIsRemoved) {
      setGiftOptionRemoved(true);
      setIsLoading(false);
      setOldPropsIsRemoved(isRemoved);
    } else {
      timeoutId = setTimeout(() => setGiftOptionRemoved(false), 500);
      setOldPropsIsRemoved(isRemoved);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [promoCodeLoadingCancelled, isRemoved, oldPropsIsRemoved]);

  const getTextLabels = () => {
    let giftOption;
    let hiddenAlert;

    switch (type) {
      case paymentTypeConstants.PROMO_CODE:
        giftOption = [id];
        break;
      default:
        giftOption = `${isGiftCard ? 'Gift card' : 'Voucher'} ending in ${endingIn}`;
        hiddenAlert = `${amount} amount applied with gift card ending in ${endingIn}. Balance to pay: ${balance}`;
    }

    return {
      giftOption,
      hiddenAlert,
    };
  };

  const onUndoItem = () => {
    setIsLoading(true);
    onUndo();
  };

  const stylesApplied = giftOptionRemoved ? styles.strikethrough : styles.removedGiftOptionText;
  const textLabels = getTextLabels();

  return (
    <div className={styles.listItem}>
      <>
        <span
          className={cx({
            [stylesApplied]: isRemoved,
          })}
        >
          {textLabels.giftOption}
        </span>

        {!isRemoved && isRemovable && (
          <PaymentDeductionsItemActionButton
            dataTest="gift-options-remove-button"
            isLoading={propsIsLoading}
            onClick={onRemove}
          >
            Remove
          </PaymentDeductionsItemActionButton>
        )}

        {showUndo && isRemoved && !orderTotalCovered && (
          <PaymentDeductionsItemActionButton
            dataTest="gift-options-undo-button"
            isLoading={isLoading && !applyGiftPaymentFailed}
            onClick={onUndoItem}
          >
            Undo
          </PaymentDeductionsItemActionButton>
        )}
      </>

      {alert && textLabels?.hiddenAlert && <HiddenAlert text={textLabels.hiddenAlert} />}

      {giftOptionRemoved && (
        <HiddenAlert text={`Your gift ${isGiftCard ? 'card' : 'voucher'} ending in ${endingIn} has been removed`} />
      )}

      <span className={cx({ [stylesApplied]: isRemoved })}>{`-${amount}`}</span>
    </div>
  );
};

export default PaymentDeductionsItem;
